import store from '../../store';
import ModalManager from '../modalManager';
import stateManager from '../user/stateManager';
import PaymentsValueSwiper from '../../components/provider/payments/PaymentsValueSwiper';
import PaymentsEducationSwiper from '../../components/provider/payments/PaymentsEducationSwiper';
import analytics from '../analytics';
import FeatureFlags from '../FeatureFlags';

/**
 * This module contains logic for showing the
 * snackbars that open the payments swipers
 */

/**
 * Feature flag for payments value swiper
 */
const PAYMENTS_VALUE_FLAG = 'value-swiper-snackbar';

/**
 * Feature flag for payments education swiper
 */
const PAYMENTS_EDUCATION_FLAG = 'education-swiper-snackbar';

/**
 * Contains feature flags, events and components
 * for the different swipers
 * @type {Object}
 */
const swipers = {
  value: {
    featureFlag: PAYMENTS_VALUE_FLAG,
    snackbarClick: 'pro_payments_value_snackbar_clicked',
    snackbarDismissed: 'pro_payments_value_snackbar_dismissed',
    component: PaymentsValueSwiper,
  },
  education: {
    featureFlag: PAYMENTS_EDUCATION_FLAG,
    snackbarClick: 'pro_payments_education_snackbar_clicked',
    snackbarDismissed: 'pro_payments_education_snackbar_dismissed',
    component: PaymentsEducationSwiper,
  },
};

/**
 * Creates promises to look up versions
 * of dynamic feature flag. ie
 * `value-swiper-snackbar-1`
 * @param  {String} flagName The feature flag base name
 * @return {Array}           An array of promises
 */
const generatePromisesForFlag = flagName => {
  const promises = [];
  let promise;
  for (let i = 0; i < 5; i++) {
    promise = FeatureFlags.isEnabled(`${flagName}-${i}`);
    promises.push(promise);
  }
  return promises;
};

/**
 * Get the highest version feature flag
 * that's enabled for this pro
 * @param  {String} flagName The feature flag base name
 * @return {Promise}
 */
const getLastEnabledFlagIndex = flagName => {
  const flagPromises = generatePromisesForFlag(flagName);
  return Promise.all(flagPromises)
    .then(flags => flags.lastIndexOf(true));
};

/**
 * Mark a snack bar dismissed
 * @param  {String} flagName The feature flag base name
 * @return {undefined}
 */
const markSnackbarDismissed = flagName => {
  getLastEnabledFlagIndex(flagName)
    .then(index => {
      const userStateKey = `${flagName}-${index}`;
      stateManager.addAction(userStateKey, true);
    });
};

/**
 * Close the snackbar and mark
 * it as dismissed in the feature flag
 * @param  {String} flag The name of the feature flag
 * @return {undefined}
 */
const closeSnackbar = flag => {
  markSnackbarDismissed(flag);
};

/**
 * Show swiper and track snackbar click
 * @param  {String} swiper 'value' or 'education'
 * @return {undefined}
 */
const showSwiper = swiper => {
  const config = swipers[swiper];
  closeSnackbar(config.featureFlag);
  ModalManager.openModal(config.component);
  analytics.track(config.snackbarClick);
};

/**
 * Props for payments value snackbar
 * @type {Object}
 */
const paymentsValueSnackbarProps = {
  header: 'Clients tip 40% more on avg. when they pay through StyleSeat.',
  confirm: {
    text: 'Learn More',
    action: () => {
      showSwiper('value');
    },
  },
  dismiss: {
    text: 'Dismiss',
    action: () => {
      const config = swipers.value;
      closeSnackbar(config.featureFlag);
      analytics.track(config.snackbarDismissed);
    },
  },
};

/**
 * Props for payments education snackbar
 * @type {Object}
 */
const paymentsEducationSnackbarProps = {
  header: 'Clients tip 40% more on avg. when they pay through StyleSeat.',
  confirm: {
    text: 'Learn More',
    action: () => {
      showSwiper('education');
    },
  },
  dismiss: {
    text: 'Dismiss',
    action: () => {
      const config = swipers.education;
      closeSnackbar(config.featureFlag);
      analytics.track(config.snackbarDismissed);
    },
  },
};

const PaymentsSwipers = {
  /**
   * Open the payments value snack bar
   * For the snack bar to show the feature flag version
   * ie, (value-swiper-snackbar-0) must be on. And that
   * flag must not have been marked as dimissed in the
   * state service
   */
  openValueSnackbar() {
    getLastEnabledFlagIndex(PAYMENTS_VALUE_FLAG)
      .then(index => {
        if (index > -1) {
          const userStateKey = `${PAYMENTS_VALUE_FLAG}-${index}`;
          return stateManager.getActionValue(userStateKey);
        }
        // Don't show snack bar
        return true;
      })
      .then(dismissed => {
        if (!dismissed) {
        // Open the snackbar
          store.dispatch.snackbar.open(paymentsValueSnackbarProps);
        }
      });
  },

  /**
   * Open the payments value snack bar
   * For the snack bar to show the feature flag version
   * ie, (education-swiper-snackbar-0) must be on. And that
   * flag must not have been marked as dimissed in the
   * state service
   */
  openEducationSnackBar() {
    getLastEnabledFlagIndex(PAYMENTS_EDUCATION_FLAG)
      .then(index => {
        if (index > -1) {
          const userStateKey = `${PAYMENTS_EDUCATION_FLAG}-${index}`;
          return stateManager.getActionValue(userStateKey);
        }
        // Don't show snack bar
        return true;
      })
      .then(dismissed => {
        if (!dismissed) {
        // Open the snackbar
          store.dispatch.snackbar.open(paymentsEducationSnackbarProps);
        }
      });
  },

  showSnackBars() {
    this.openValueSnackbar();
    this.openEducationSnackBar();
  },
};

export default PaymentsSwipers;
